/** Side length of the square fitted around a single cited point, in meters. */
export const SINGLE_CITED_POINT_SQUARE_M = 1000;

const METERS_PER_DEGREE_LAT = 111_320;

export type LngLatBoundsLike = {
  west: number;
  south: number;
  east: number;
  north: number;
};

/**
 * Geographic bounds for cited points only. One point becomes a 1 km square;
 * several points use their tight box. Context points must not be passed in.
 */
export function fitCitedPointBounds(
  points: Array<{ latitude: number; longitude: number }>,
): LngLatBoundsLike | null {
  if (points.length === 0) return null;
  if (points.length === 1) {
    const { latitude, longitude } = points[0];
    const half = SINGLE_CITED_POINT_SQUARE_M / 2;
    const dLat = half / METERS_PER_DEGREE_LAT;
    const cosLat = Math.cos((latitude * Math.PI) / 180);
    const dLon = half / (METERS_PER_DEGREE_LAT * (Math.abs(cosLat) < 1e-6 ? 1e-6 : cosLat));
    return {
      west: longitude - dLon,
      south: latitude - dLat,
      east: longitude + dLon,
      north: latitude + dLat,
    };
  }
  let west = points[0].longitude;
  let south = points[0].latitude;
  let east = points[0].longitude;
  let north = points[0].latitude;
  for (const point of points) {
    west = Math.min(west, point.longitude);
    south = Math.min(south, point.latitude);
    east = Math.max(east, point.longitude);
    north = Math.max(north, point.latitude);
  }
  return { west, south, east, north };
}

/**
 * Identity of the citation focus and highlight set, without the plotted points.
 */
export function mapFocusFitKey(
  focusIds: Array<string | number> = [],
  highlightIds: Array<string | number> = [],
): string {
  const focusPart = [...focusIds].map(String).sort().join(',');
  const highlightPart = [...highlightIds].map(String).sort().join(',');
  return `${focusPart}#${highlightPart}`;
}

/**
 * Identity of the cited set used to decide whether a map should refit.
 *
 * Point array identity changes on every parent render; this key stays stable
 * when the coordinates and focus set have not.
 */
export function citationMapFitKey(
  points: Array<{ id: string; latitude: number; longitude: number }>,
  focusIds: Array<string | number> = [],
  highlightIds: Array<string | number> = [],
): string {
  const pointPart = [...points]
    .map((point) => `${point.id}:${point.latitude}:${point.longitude}`)
    .sort()
    .join('|');
  return `${pointPart}#${mapFocusFitKey(focusIds, highlightIds)}`;
}

/**
 * Whether a newly arrived point batch may move the camera.
 *
 * A pan or zoom while points are still loading keeps the camera where the
 * user left it. The first observation of a focus set is not a change; a later
 * citation focus or highlight still recenters.
 */
export function shouldAutoFitMapCamera(
  userAdjustedCamera: boolean,
  previousFocusKey: string | null,
  nextFocusKey: string,
): boolean {
  if (!userAdjustedCamera) return true;
  return previousFocusKey !== null && previousFocusKey !== nextFocusKey;
}

export type MapPointRole = 'highlight' | 'cited' | 'context';

/**
 * True when *id* is one of *focusIds*, or a trip pin (`4:origin`) of an id in that set.
 */
export function mapPointMatchesFocus(
  id: string | number,
  focusIds: Array<string | number>,
): boolean {
  const key = String(id);
  const targets = new Set(focusIds.map(String));
  if (targets.has(key)) return true;
  const colon = key.indexOf(':');
  if (colon <= 0) return false;
  return targets.has(key.slice(0, colon));
}

/** Highlight rings, cited stays full color, context is grayed. */
export function mapPointRole(
  id: string | number,
  focusIds: Array<string | number>,
  highlightIds: Array<string | number>,
): MapPointRole {
  if (mapPointMatchesFocus(id, highlightIds)) return 'highlight';
  if (mapPointMatchesFocus(id, focusIds)) return 'cited';
  return 'context';
}
